<template>
	<v-card
		width="240"
		height="208"
		bg-color="contained"
		variant="tonal"
		class="ma-2"
		ripple
		:disabled="alreadyInWorkspace()"
		@click="importInstance"
	>
		<v-card-title class="text-primary">
			{{ searchResultTerm(compProps.instanceDataobject) }}
		</v-card-title>
		<v-card-text class="pa-0">
			<v-list-item
				v-if="displayedAttributes.length > 0"
				v-for="iAttr in displayedAttributes.slice(0, 3)"
				:key="iAttr.id"
			>
				<v-list-item-content>
					<v-list-item-title>
						{{ instanceAttributeTerm(iAttr) }}
					</v-list-item-title>
					<v-list-item-subtitle>{{ displayedAttrVal(iAttr) }}</v-list-item-subtitle>
				</v-list-item-content>
			</v-list-item>
			<v-list-item v-else>
				<v-list-item-content>
					<v-list-item-title> No attributes to display </v-list-item-title>
				</v-list-item-content>
			</v-list-item>
		</v-card-text>
	</v-card>
</template>

<script setup lang="ts">
import { PropType } from "vue";
import { type InstanceDataobject, type InstanceAttribute } from "@lr-notitia/notitia-operator/schemas";
import { useGraphStore } from "../../../../store/graph";
import { useSymbolsStore } from "../../../../store/symbols";
import { useDomainStore } from "../../../../store/domain";
import { useInstanceWorkspacesStore } from "../../../../store/i-workspace";
import { api } from "@lr-notitia/notitia-operator/client";
import { handleGraphModification } from "../../../../store/graph/utils";

const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();
const domainStore = useDomainStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();

const emits = defineEmits(["imported"]);
const compProps = defineProps({
	instanceDataobject: {
		type: Object as PropType<InstanceDataobject>,
		required: true,
	},
	displayedAttributes: {
		type: Array as PropType<InstanceAttribute[]>,
		default: () => [],
	},
	parentInstanceDataobjectId: {
		type: String,
		required: false,
		default: null,
	},
	sourceInstanceWorkspaceId: {
		type: String,
		required: true,
	},
});

function searchResultTerm(result: InstanceDataobject) {
	const concept = graphStore.concept.dataobject.byId(result.concept);
	if (!concept) return "Unknown Concept";
	return symbolsStore.term({
		id: concept.refersTo,
		terminology: concept.terminology,
		lang: domainStore.languagePreference,
	});
}
function instanceAttributeTerm(iAttr: InstanceAttribute) {
	const concept = graphStore.concept.attribute.byId(iAttr.concept);
	if (!concept) return "Unknown Concept";
	return symbolsStore.term({
		id: concept.refersTo,
		terminology: concept.terminology,
		lang: domainStore.languagePreference,
	});
}
function displayedAttrVal(iAttr: InstanceAttribute) {
	// Arrays
	if (Array.isArray(iAttr.value)) {
		// Enum Item
		if (isEnumItemId(iAttr.value[0])) {
			return iAttr.value
				.map((enumItemId: string) => {
					const enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
					if (!enumItem) return "Unknown Enum Item";
					return symbolsStore.term({
						id: enumItem.refersTo,
						terminology: enumItem.terminology,
						lang: domainStore.languagePreference,
					});
				})
				.join(", ");
		}
		// Strings or Numbers
		else {
			return iAttr.value.join(", ");
		}
	}
	// Non-arrays
	else {
		// Enum Item
		if (isEnumItemId(iAttr.value)) {
			const enumItem = graphStore.descriptor.enumItem.byId(iAttr.value);
			if (!enumItem) return "Unknown Enum Item";
			return symbolsStore.term({
				id: enumItem.refersTo,
				terminology: enumItem.terminology,
				lang: domainStore.languagePreference,
			});
		}
		// Strings or Numbers
		else {
			return iAttr.value;
		}
	}
}
function isEnumItemId(value: string): boolean {
	return !!graphStore.descriptor.enumItem.byId(value);
}

function importInstance() {
	if (!instanceWorkspacesStore.openedWorkspace) return;
	api()
		.domain(domainStore.domain.id)
		.workspace(instanceWorkspacesStore.openedWorkspace.id)
		.dataobjects()
		.importExisting({
			instanceDataobject: compProps.instanceDataobject,
			parentInstanceDataobjectId: compProps.parentInstanceDataobjectId || undefined,
			sourceInstanceWorkspaceId: compProps.sourceInstanceWorkspaceId,
		})
		.then((d) => {
			instanceWorkspacesStore.nodePositions[instanceWorkspacesStore.openedWorkspace?.id || ""][
				compProps.instanceDataobject.id
			] = {
				x: 10,
				y: 10,
				scale: 1,
			};
			handleGraphModification(d.modification, d.metadata);
			emits("imported");
		});
}

function alreadyInWorkspace() {
	return !!instanceWorkspacesStore.workspaceGraph.nodes.find((n) => n.id === compProps.instanceDataobject.id);
}
</script>
